<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { Component } from 'vue';

import { Button } from '@clovyr/bed';
import { IconPosition } from '@clovyr/bed/components/controls/types';
import { formatWNextPrice } from '@clovyr/bed/composables/usePrice';
import type { Manifest } from '@clovyr/pollen/manifest';
import { ComputeProviderName } from '@clovyr/pollen/types/Host';

import LaunchWizardProgress, {
  type LaunchWizardHostingType,
  type LaunchWizardStepKey,
} from '@/components/blocks/LaunchWizardProgress.vue';
import Modal, { type ModalSize } from '@/components/elements/Modal.vue';
import Spinner from '@/components/elements/Spinner.vue';
import ArrowBackIcon from '@/components/icons/ArrowBackIcon.vue';
import ArrowForwardsIcon from '@/components/icons/ArrowForwardsIcon.vue';
import { useLauncherStore } from '@/stores/launcher_store';
import { useMediaQueryStore } from '@/stores/media_query_store';
import { LauncherState } from '@/stores/types';
import BillingPeriod from '@/views/AppLauncher/BillingPeriod.vue';
import ChooseHost from '@/views/AppLauncher/ChooseHost.vue';
import HostCredentials from '@/views/AppLauncher/HostCredentials.vue';
import LaunchSettings from '@/views/AppLauncher/LaunchSettings.vue';
import ReviewSetup from '@/views/AppLauncher/ReviewSetup.vue';

const props = withDefaults(
  defineProps<{ app: Manifest; parentRoute: string; embedded?: boolean }>(),
  {
    embedded: false,
  },
);

const router = useRouter();
const route = useRoute();
const launcherStore = useLauncherStore();
const {
  status,
  selectedHost,
  hostSelectionIntent,
  selectedComputeProviderConfig,
  isNextEnabled,
  isBackEnabled,
  wnextAppPrice,
  billingPeriodLabel,
  showBillingPeriodStep,
} = storeToRefs(launcherStore);
const { initialize, next, back } = launcherStore;

/**
 * Maps the launcher state machine's current status to a Launch Wizard
 * progress-indicator step. 'Launch' is not its own bubble -- it's the action
 * taken from the Review step.
 */
const progressStep = computed<LaunchWizardStepKey>(() => {
  switch (status.value) {
    case LauncherState.Settings:
      return 'setup';
    case LauncherState.ChooseHost:
      return 'host';
    case LauncherState.HostCredentials:
      return 'configure-host';
    case LauncherState.BillingPeriod:
      return 'billing';
    case LauncherState.Review:
    case LauncherState.Launching:
    default:
      return 'review';
  }
});

/**
 * Resolves the hosting path for the stepper. `hostSelectionIntent` reflects the
 * user's choice on the Setup page (e.g. 'Choose another host') even while
 * selectedHost is momentarily cleared, so the Host step appears immediately
 * instead of only once the ChooseHost view is reached.
 */
const progressHostingType = computed<LaunchWizardHostingType>(() => {
  if (status.value === LauncherState.ChooseHost || status.value === LauncherState.HostCredentials) {
    return 'byoh';
  }
  if (selectedHost.value && selectedHost.value.id !== ComputeProviderName.WNext) {
    return 'byoh';
  }
  return hostSelectionIntent.value;
});

// initializer launcher state machine
initialize(props.app);

/**
 * Maps the current launcher state to the proper view & button labels
 */
type StateToComponent = {
  component: Component;
  percentComplete?: string;
  backLabel?: string;
  nextLabel?: string;
  btnLabel?: string;
};

let nonWnextHostChosen = false;

/** Single source of truth for 'is the active hosting choice Clovyr Managed?' */
const isWNext = computed(() => selectedHost.value?.id === ComputeProviderName.WNext);

const nextLabelReview = computed(() => {
  if (selectedComputeProviderConfig.value !== undefined || isWNext.value) {
    return true;
  }
  return false;
});

const { isMobile } = storeToRefs(useMediaQueryStore());

/**
 * Clovyr Managed hosting on Clovyr AI Cloud goes to Billing next, not Review
 * directly, so the button must say so instead of always reading 'Review'.
 */
const settingsNextLabel = computed(() => {
  if (isWNext.value) {
    return showBillingPeriodStep.value ? 'Billing' : 'Review';
  }

  if (nextLabelReview.value) {
    return 'Review';
  }

  if (isMobile.value) {
    return 'Host';
  }
  return 'Choose Host';
});

const reviewBackLabel = computed(() => {
  if (showBillingPeriodStep.value) {
    return 'Billing';
  }
  if (isMobile.value) {
    if (nonWnextHostChosen) {
      return 'Config Host';
    }
    return 'Host';
  }
  if (nonWnextHostChosen) {
    return 'Configure Host';
  }
  return 'Choose Host';
});

const chooseHostNextLabel = computed(() => {
  if (isWNext.value) {
    return 'Review';
  }

  if (isMobile.value) {
    return 'Config';
  }
  return 'Configure Host';
});

/**
 * The Billing back button must point at whichever step precedes it in the
 * user's active hosting path: Setup for Clovyr Managed, Host for BYOH.
 */
const billingBackLabel = computed(() => {
  if (progressHostingType.value === 'clovyr') {
    return 'Setup';
  }
  if (isMobile.value) {
    return nonWnextHostChosen ? 'Config Host' : 'Host';
  }
  return nonWnextHostChosen ? 'Configure Host' : 'Choose Host';
});

const stc = computed<StateToComponent>(() => {
  switch (status.value) {
    case LauncherState.Settings:
      return {
        component: LaunchSettings,
        percentComplete: nextLabelReview.value ? '50' : '25',
        nextLabel: settingsNextLabel.value,
        btnLabel: 'Cancel',
      };
    case LauncherState.ChooseHost:
      return {
        component: ChooseHost,
        percentComplete: '50',
        backLabel: 'Setup',
        nextLabel: chooseHostNextLabel.value,
        btnLabel: 'Cancel',
        nonWnextHostChosen: (nonWnextHostChosen = false),
      };
    case LauncherState.HostCredentials:
      return {
        component: HostCredentials,
        percentComplete: isWNext.value ? '' : '60',
        backLabel: isMobile.value ? 'Host' : 'Choose Host',
        nextLabel: 'Review',
        btnLabel: 'Cancel',
        nonWnextHostChosen: (nonWnextHostChosen = true),
      };
    case LauncherState.BillingPeriod:
      return {
        component: BillingPeriod,
        percentComplete: '66',
        backLabel: billingBackLabel.value,
        nextLabel: 'Review',
        btnLabel: 'Cancel',
      };
    case LauncherState.Review:
      return {
        component: ReviewSetup,
        percentComplete: '75',
        backLabel: reviewBackLabel.value,
        nextLabel: 'Launch',
        btnLabel: 'Cancel',
      };
    case LauncherState.Launching:
      return {
        component: Spinner,
        btnLabel: 'Close',
      };

    default:
      return {
        component: LaunchSettings,
      };
  }
});

const isLaunching = computed(() => status.value === LauncherState.Launching);

const showLauncher = ref(props.embedded);
const emit = defineEmits(['close']);

const handleClose = () => {
  emit('close');
  if (props.embedded) {
    return;
  }

  if (window.history.state.back) {
    // If we have pushstate history, use it to return to where we came from (detail page or tag page, etc)
    void router.back();
  } else {
    // Otherwise we navigated here directly and we force back to the app detail page for this app.
    // From the app detail page, navigating 'back' will return them to wherever they came from,
    // either an external site or new tab page, etc.
    void router.push({ path: props.parentRoute, hash: route.hash, replace: true });
  }
};

onBeforeRouteLeave((to, from) => {
  if (to.name === 'AppDetail') {
    to.meta = {
      ...to.meta,
      ...from.meta,
    };
  }
});

// Tight theme except when showing only the spinner
const modalSize = computed<ModalSize>(() =>
  status.value === LauncherState.Launching ? '' : 'tight',
);

onMounted(async () => {
  if (props.embedded) {
    return;
  }

  return nextTick(() => {
    showLauncher.value = true;
  });
});

const formattedWnextAppPrice = computed(() => formatWNextPrice(wnextAppPrice.value));
</script>

<template>
  <div v-if="showLauncher && embedded" class="launcher-inline">
    <LaunchWizardProgress
      v-if="!isLaunching"
      class="launcher-inline__progress"
      :current-step="progressStep"
      :hosting-type="progressHostingType"
    />

    <component :is="stc.component" />

    <div v-if="!!stc.nextLabel" class="launcher-inline__footer">
      <a @click.prevent="handleClose" class="text-link">{{ stc.btnLabel }}</a>

      <div class="footer-actions">
        <div class="footer-pricing" v-if="isWNext && app.metadata.id !== 'clovyr-ai-cloud'">
          <span class="footer-price">${{ formattedWnextAppPrice }}</span
          ><span class="footer-price-sub">/mo after free trial</span>
        </div>
        <div class="footer-pricing" v-else-if="isWNext && app.metadata.id === 'clovyr-ai-cloud'">
          <span class="footer-price">${{ formattedWnextAppPrice }}</span>
          <span class="footer-price-sub"
            >/{{ billingPeriodLabel === 'Monthly' ? 'mo' : 'yr' }}</span
          >
        </div>
        <div class="footer-buttons">
          <Button
            v-if="!!stc.backLabel"
            :disabled="!isBackEnabled"
            :label="stc.backLabel"
            :iconPosition="IconPosition.Left"
            :reversed="true"
            @click="back"
          >
            <ArrowBackIcon />
          </Button>
          <Button
            :disabled="!isNextEnabled"
            :label="stc.nextLabel"
            :iconPosition="IconPosition.Right"
            :reversed="true"
            @click="next"
          >
            <ArrowForwardsIcon />
          </Button>
        </div>
      </div>
    </div>
  </div>

  <Modal
    v-else-if="showLauncher"
    :size="modalSize"
    maxOnMobile
    stickyFooter
    @close="handleClose"
    hide-close
  >
    <template v-slot:progress-bar>
      <LaunchWizardProgress
        v-if="!isLaunching"
        class="launcher-inline__progress launcher-modal__progress"
        :current-step="progressStep"
        :hosting-type="progressHostingType"
      />
    </template>

    <template v-slot:body>
      <component :is="stc.component" />
    </template>

    <template v-if="!!modalSize" v-slot:controls-left>
      <a @click.prevent="handleClose" class="text-link">{{ stc.btnLabel }}</a>
    </template>

    <template v-if="!!stc.nextLabel" v-slot:controls-right>
      <div class="footer-actions">
        <div class="footer-pricing" v-if="isWNext && app.metadata.id !== 'clovyr-ai-cloud'">
          <span class="footer-price">${{ formattedWnextAppPrice }}</span
          ><span class="footer-price-sub">/mo after free trial</span>
        </div>
        <div class="footer-pricing" v-else-if="isWNext && app.metadata.id === 'clovyr-ai-cloud'">
          <span class="footer-price">${{ formattedWnextAppPrice }}</span>
          <span class="footer-price-sub"
            >/{{ billingPeriodLabel === 'Monthly' ? 'mo' : 'yr' }}</span
          >
        </div>
        <div class="footer-buttons">
          <Button
            v-if="!!stc.backLabel"
            :disabled="!isBackEnabled"
            :label="stc.backLabel"
            :iconPosition="IconPosition.Left"
            :reversed="true"
            @click="back"
          >
            <ArrowBackIcon />
          </Button>
          <Button
            :disabled="!isNextEnabled"
            :label="stc.nextLabel"
            :iconPosition="IconPosition.Right"
            :reversed="true"
            @click="next"
          >
            <ArrowForwardsIcon />
          </Button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.launcher-inline__progress {
  padding: grid-vars.space(2) 0;
}

// Match the horizontal padding of the tight launch wizard modal body.
.launcher-modal__progress {
  padding-right: grid-vars.space(4);
  padding-left: grid-vars.space(4);

  @include breakpoints.media-breakpoint-up(md) {
    padding-right: grid-vars.space(8);
    padding-left: grid-vars.space(8);
  }

  @include breakpoints.media-breakpoint-down(xs) {
    padding-right: grid-vars.space(1);
    padding-left: grid-vars.space(1);
  }
}

.text-link {
  color: colors.color(action, primary);
  cursor: pointer;

  background: none;
  border: none;
  font: inherit;
  padding: 0;
}

.text-link:hover {
  text-decoration: none;
}

.footer-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.75rem;
  width: 100%;
}

.launcher-inline {
  width: 100%;
  border-radius: 12px;
  background: transparent;
  overflow: hidden;
}

.launcher-inline__footer {
  padding: 1.5rem 1rem;
}

@media (min-width: 850px) {
  .footer-actions {
    flex-direction: row;
    justify-content: space-between;
  }
  .footer-pricing {
    margin-right: 1rem;
  }
}

.footer-pricing {
  font-size: 18px;
  font-weight: 600;
  color: colors.color(action, primary);
  line-height: 1.4;
  min-width: fit-content;
}

.footer-price {
  font-size: 20px;
  font-weight: 700;
  color: colors.color(action, primary);
}

.footer-price-sub {
  font-size: 16px;
  font-weight: 400;
  color: #ccc;
}

.footer-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
}
</style>
